<template>
  <div
    v-if="bonusContent"
    class="stage-scale"
    :class="rootClasses">
    <ul class="stage-scale__steps">
      <li
        v-for="(stage, index) in bonusContent.stageTiers"
        :key="index"
        class="stage-scale__step"
        :class="getNodeClass(stage)">
        <div class="stage-scale__progress-bar">
          <div class="stage-scale__progress-fill"
               :style="{ width: getProgressBarWidth(index) + '%' }" />
        </div>
        <div class="stage-scale__dot" />
        <div class="stage-scale__label">
          <span class="stage-scale__label-text">
            {{ isMiniCart ? (index + 1) : stage.name }}
          </span>
        </div>
      </li>
    </ul>
    <div
      class="stage-scale__bonus-next-stage"
      v-if="getMoreTextHtml"
      v-html="getMoreTextHtml" />
    <span v-if="isMiniCart" class="stage-scale__bonus-info-text">
      See freebies on the cart page
    </span>
  </div>
</template>

<script lang="ts">
import { useCartStore } from "~/stores/cart";
import type { BonusContent } from '~/stores/types/cart.js';

export default {
  setup() {
    const cartStore = useCartStore()

    return { cartStore }
  },
  props: {
    isMiniCart: {
      type: Boolean
    },
  },
  computed: {
    getMoreTextHtml () {
      if (! this.bonusContent?.amountTillNextStage) {
        return false
      }

      if (this.isMiniCart) {
        // check if on last tier
        if (this.bonusContent.currentTier == this.bonusContent.stageTiers.length) {
          return false
        }

        // check if on first tier
        if (this.bonusContent.currentTier == 0) {
          return `Order for <b>${this.bonusContent.amountTillNextStage}</b> more to get free content!`
        }

        return `Order for <b>${this.bonusContent.amountTillNextStage}</b> more to get more for free!`
      }

      return this.bonusContent.currentTierDetails.replace('{remaining_amount}', this.bonusContent.amountTillNextStage)
    },
    bonusContent (): BonusContent|null {
      return this.cartStore?.getCurrentCart?.bonusContent
    },
    rootClasses () {
      return {
        'stage-scale--mini-cart': this.isMiniCart,
      };
    },
  },
  methods: {
    getNodeClass(stage) {
      return {
        'stage-scale__step--achieved': this.bonusContent.currentBalanceEuro >= stage.quota,
      }
    },
    getProgressBarWidth(index) {
      const cartValue = this.bonusContent.currentBalanceEuro
      const previousStage = this.bonusContent.stageTiers[index - 1]
      const currentStage = this.bonusContent.stageTiers[index]
      let progress = 100

      if (cartValue < currentStage.quota) {
        const previousQuota = previousStage ? previousStage.quota : 0
        const valueMinusPrevious = cartValue - previousQuota
        const diffPreviousCurrent = currentStage.quota - previousQuota

        progress = Math.max(valueMinusPrevious / diffPreviousCurrent * 100, 0)
      }

      return progress
    }
  },
}
</script>

<style lang="scss">
@use "sass:math";
@use "~/common/scss/helpers";

.stage-scale {
  $root: &;

  --bar-height: #{helpers.px-to-rem(8)};
  --dot-height: #{helpers.px-to-rem(20)};
  --bar-size: calc(100% - #{helpers.px-to-rem(6)});

  display: grid;
  grid-template: "steps" auto "orderForMore" auto "seeAllInCart" auto / 100%;
  margin: 0.7rem 0 1.7rem 0;

  &--mini-cart {
    --bar-height: #{helpers.px-to-rem(5)};
    --dot-height: #{helpers.px-to-rem(12)};
    --bar-size: calc(100% - #{helpers.px-to-rem(3)});

    grid-template: "orderForMore" auto "steps" auto "seeAllInCart" auto / 100%;
    margin: 0 0;

    #{$root}__dot {
      border-width: 2px;
    }
  }

  &__steps {
    display: flex;
    grid-area: steps;
    flex-flow: row nowrap;
    list-style: none;
    padding: 0 0;
    margin-bottom: 2rem;

    #{$root}--mini-cart & {
      margin-bottom: .25rem;
    }
  }

  &__step {
    --bar-color: #db7a0a;
    --dot-color: #c3c3c3;
    --color: #c3c3c3;

    align-items: center;
    display: grid;
    flex: 1 1 auto;
    grid-template: "bar dot" var(--dot-height) "space label" 1rem / 1fr var(--dot-height);

    &:not(:first-child) #{$root}__progress-bar {
      transform: translateX(#{helpers.px-to-rem(-3)});
    }

    &--achieved {
      --bar-color: #8fbe60;
      --dot-color: #8fbe60;
      --color: #000;
    }
  }

  &__dot {
    background-color: var(--dot-color);
    border-radius: 50%;
    border: 3px solid #FFF;
    grid-area: dot;
    height: var(--dot-height);
    width: var(--dot-height);
    z-index: 1;
  }

  &__label {
    justify-content: center;
    color: var(--color);
    display: flex;
    font-size: helpers.px-to-rem(12);
    grid-area: label;
    position: relative;
    height: 1rem;
  }

  &__label-text {
    position: absolute;
    white-space: nowrap;
  }

  &__progress-bar {
    background-color: #c3c3c3;
    border-radius: 5px;
    grid-area: 1 / 1 / 2 / 3;
    height: var(--bar-height);
    width: var(--bar-size);
  }

  &__progress-fill {
    background-color: var(--bar-color);
    border-radius: 5px;
    height: 100%;
    transition: width .25s;
  }

  &__bonus-next-stage {
    grid-area: orderForMore;
    margin-top: 2rem;

    hr {
      margin: 1rem 0;
    }
  }

  &__bonus-info-text {
    grid-area: seeAllInCart;
  }
}
</style>
